import { ChevronDownIcon, SirenIcon } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { fmtDateTimeInZone } from '@/lib/format';
import type { WeatherAlert } from '@/lib/types';
import { cn } from '@/lib/utils';
import { ALERT_CATEGORY_LABEL, SEVERITY_LABEL, alertTone } from './describe';

/**
 * Official alerts for a site, most serious first. `compact` is the one-line banner for the Overview; the full list
 * shows the issuing office, the window and the service's own text behind a disclosure.
 */
export function WeatherAlertList({ alerts, tz, compact, className }: { alerts: WeatherAlert[]; tz: string; compact?: boolean; className?: string }) {
  if (alerts.length === 0) return null;
  if (compact) {
    const top = alerts[0]!;
    const tone = alertTone(top.severity);
    return (
      <div className={cn('flex items-start gap-3 border-l-2 border px-4 py-3', tone.border, tone.bg, className)} role="status">
        <SirenIcon className={cn('mt-0.5 size-4 shrink-0', tone.text)} aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium">{top.event}</span>
          <span className="text-muted-foreground">
            {' '}
            · {top.sender}
            {top.ends ? ` · until ${fmtDateTimeInZone(top.ends, tz)}` : ''}
            {alerts.length > 1 ? ` · +${alerts.length - 1} more` : ''}
          </span>
        </div>
      </div>
    );
  }
  return (
    <ul className={cn('grid gap-2', className)} aria-label="Weather alerts">
      {alerts.map((a) => (
        <AlertRow key={a.id} a={a} tz={tz} />
      ))}
    </ul>
  );
}

function AlertRow({ a, tz }: { a: WeatherAlert; tz: string }) {
  const [open, setOpen] = useState(false);
  const tone = alertTone(a.severity);
  return (
    <li className={cn('border-l-2 border', tone.border, tone.bg)}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-3 px-4 py-3 text-left" aria-expanded={open}>
        <SirenIcon className={cn('mt-0.5 size-4 shrink-0', tone.text)} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{a.event}</span>
            <Badge variant={tone.badge}>{SEVERITY_LABEL[a.severity]}</Badge>
            <Badge variant="outline">{ALERT_CATEGORY_LABEL[a.category]}</Badge>
          </span>
          <span className="text-muted-foreground mt-0.5 block text-xs">
            {a.sender} · from {fmtDateTimeInZone(a.starts, tz)}
            {a.ends ? ` until ${fmtDateTimeInZone(a.ends, tz)}` : ', no end given'}
          </span>
        </span>
        <ChevronDownIcon className={cn('text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && <p className="text-muted-foreground border-t px-4 py-3 text-xs leading-relaxed whitespace-pre-line">{a.description}</p>}
    </li>
  );
}
